/**
 * New-request wizard (design-spec/05 Screen 02, ux-flows §1.D) — five short, forgiving steps:
 * who → relationship → email → note → review, then a warm confirmation.
 *
 * The load-bearing screen is step 3 ("reach them"): the candidate hands over a contact, reassured by
 * the persistent motif "We contact them — not you." Validation is inline per ux-flows §1.D edge
 * cases — missing/invalid email, duplicate detection (client soft-check + the server's
 * authoritative 409), and self-referee (server-enforced, surfaced well). The server enforces
 * suppression + velocity; those surface as banners.
 *
 * Web delta (DESIGN-NOTES): the bottom CTA is a sticky thumb-zone bar (AppShell footer), not a
 * phone-anchored button.
 *
 * Step 3 also carries the referee-permission attestation — the candidate's own statement that this
 * person would be OK to hear from them. It sits with the contact details because that is where the
 * candidate is handing over someone else's address; the claim belongs next to its subject, not two
 * steps later on a summary. It is NOT the referee's consent (see PermissionAttestation).
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, CandidateApiError } from '@/lib/api-client';
import {
  permissionStatement,
  refereeObject,
  unconfirmedMessage,
  SERVER_REFUSED_MESSAGE,
} from '@/lib/attestation-copy';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { TextField } from '@/components/ui/field';
import { TextArea } from '@/components/ui/textarea';
import { WizardProgress } from '@/components/request/WizardProgress';
import { PermissionAttestation } from '@/components/request/PermissionAttestation';
import { RelationshipPicker, type Relationship } from '@/components/request/RelationshipPicker';

interface WizardValues {
  refereeName: string;
  relationshipContext: string;
  companyName: string;
  refereeEmail: string;
  personalNote: string;
  /** The candidate's assertion about this person — never defaulted true, never pre-ticked. */
  refereePermissionConfirmed: boolean;
}

const STEP_COUNT = 5;

/** The step that owns the referee's contact details + the permission attestation. */
const REACH_STEP = 2;

const SUGGESTED_NOTE =
  'Hi — would you be able to be a reference for me? It only takes a few minutes, and it would mean a lot. Thank you.';

/** Loose email gate matching the backend's — accepts odd-but-valid addresses, rejects the obvious. */
function looksLikeEmail(email: string): boolean {
  const at = email.indexOf('@');
  return at > 0 && at < email.length - 1 && email.indexOf('.', at) > at;
}

const MailIcon = (
  <svg viewBox="0 0 24 24" fill="none" width={17} height={17} aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);
const ShieldIcon = (
  <svg viewBox="0 0 24 24" fill="none" width={18} height={18} aria-hidden="true" className="flex-none">
    <path d="M12 2l7 4v6c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** The persistent trust motif — repeated, never buried (04-copy). */
function Assurance() {
  return (
    <p className="mt-5 flex items-center gap-2.5 rounded-md bg-sage-tint px-3.5 py-3 font-body text-[13px] font-medium text-sage-text">
      {ShieldIcon}
      We contact them — not you.
    </p>
  );
}

export function NewRequest() {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [banner, setBanner] = useState<string | null>(null);
  const [sent, setSent] = useState<null | { name: string; email: string }>(null);

  const form = useForm<WizardValues>({
    defaultValues: {
      refereeName: '',
      relationshipContext: '',
      companyName: '',
      refereeEmail: '',
      personalNote: SUGGESTED_NOTE,
      // Unticked, always. A pre-ticked box would record an attestation the candidate never made,
      // which is the one thing this field exists to avoid.
      refereePermissionConfirmed: false,
    },
  });
  const { register, watch, setValue, setError, clearErrors, getValues, formState } = form;
  const values = watch();
  const firstName = refereeObject(values.refereeName);

  // Focus is moved to the attestation whenever it is refused — client-side or by the server — so a
  // screen-reader user lands on the control that needs them, not on a message about it. The nonce
  // (rather than "an error exists") makes a repeated refusal re-focus instead of going quiet.
  const attestationRef = useRef<HTMLInputElement | null>(null);
  const [attestationRefusals, setAttestationRefusals] = useState(0);
  useEffect(() => {
    if (attestationRefusals > 0) attestationRef.current?.focus();
  }, [attestationRefusals]);

  /**
   * Refuse the send on the attestation: clear the tick, put the message on the control itself, land
   * the wizard on the step that shows it, and take focus. Clearing the tick matters on the server
   * path — if the server says it did not record the attestation, the UI must stop claiming it did.
   */
  function refusePermission(message: string) {
    setValue('refereePermissionConfirmed', false);
    setError('refereePermissionConfirmed', { message });
    setStep(REACH_STEP);
    setAttestationRefusals((n) => n + 1);
  }

  function goTo(target: number) {
    setBanner(null);
    setStep(target);
  }

  /** Validate the active step; return true to advance. */
  function validateStep(): boolean {
    clearErrors();
    const v = getValues();
    if (step === 0) {
      if (!v.refereeName.trim()) {
        setError('refereeName', { message: 'Add their name — it appears on the letter.' });
        return false;
      }
    }
    if (step === 1) {
      if (!v.relationshipContext) {
        setError('relationshipContext', { message: 'Pick how you worked together.' });
        return false;
      }
    }
    if (step === REACH_STEP) {
      const hasEmail = v.refereeEmail.trim().length > 0;
      if (!hasEmail) {
        setError('refereeEmail', { message: `Add ${firstName}’s email — that’s how we send the invite.` });
        return false;
      }
      if (!looksLikeEmail(v.refereeEmail.trim())) {
        setError('refereeEmail', { message: 'That doesn’t look like an email address.' });
        return false;
      }
      // The attestation is checked here, ahead of the duplicate soft-check: every other repair on
      // this step is one the candidate makes here, whereas the duplicate hint sends them back to
      // step 1. Failing that one first would leave an unticked box behind, unmentioned.
      if (!v.refereePermissionConfirmed) {
        refusePermission(unconfirmedMessage(v.refereeName));
        return false;
      }
      // Client-side duplicate soft-check against the ledger (server 409 is authoritative on send).
      const ledger = queryClient.getQueryData<{ refereeName: string }[]>(['ledger']);
      const dup = ledger?.some((c) => c.refereeName.trim().toLowerCase() === v.refereeName.trim().toLowerCase());
      if (dup) {
        setError('refereeName', { message: `You’ve already asked ${firstName}. Check your ledger.` });
        setStep(0);
        return false;
      }
    }
    return true;
  }

  function next() {
    if (validateStep()) goTo(step + 1);
  }

  /** Map a server ProblemDetails code to the right field / banner and the step to land on. */
  function surfaceSendError(err: CandidateApiError) {
    switch (err.code) {
      case 'self_referee':
        setError('refereeEmail', { message: 'That’s your own address — ask someone who can speak to your work.' });
        setStep(REACH_STEP);
        break;
      case 'duplicate_request':
        setError('refereeName', { message: `You’ve already asked ${firstName}. Check your ledger.` });
        setStep(0);
        break;
      case 'referee_suppressed':
        setBanner(`We can’t send to that address. Try another way to reach ${firstName}.`);
        setStep(REACH_STEP);
        break;
      case 'rate_limited':
        setBanner('You’ve sent a lot of requests just now. Give it a little while and try again.');
        break;
      // Its own code, not invalid_request, so this can land on the confirmation instead of the
      // email field. Reaching it means the client and the server disagree about what was sent —
      // the server is right, so the tick goes away and the candidate is asked again.
      case 'referee_permission_required':
        refusePermission(SERVER_REFUSED_MESSAGE);
        break;
      case 'invalid_request':
        setBanner('Something in the details needs a tweak. Check the name and email.');
        setStep(REACH_STEP);
        break;
      default:
        setBanner('We couldn’t send that just now. Try again in a moment.');
    }
  }

  async function onSend() {
    setBanner(null);
    const v = getValues();
    // The send refuses on its own account, not only because step 3's gate stands in front of it.
    // Two guards look redundant today; the point is that no later rewiring of the steps can put a
    // request on the wire without the attestation, and the API is never called to find out.
    if (!v.refereePermissionConfirmed) {
      refusePermission(unconfirmedMessage(v.refereeName));
      return;
    }
    try {
      await apiClient.createRequest({
        refereeName: v.refereeName,
        refereeEmail: v.refereeEmail,
        relationshipContext: v.relationshipContext || null,
        companyName: v.companyName || null,
        personalNote: v.personalNote || null,
        // The value, not a literal true — the payload reports what the candidate actually said.
        refereePermissionConfirmed: v.refereePermissionConfirmed,
      });
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      setSent({ name: v.refereeName.trim(), email: v.refereeEmail.trim() });
    } catch (caught) {
      if (caught instanceof CandidateApiError) surfaceSendError(caught);
      else setBanner('We couldn’t send that just now. Try again in a moment.');
    }
  }

  if (sent) {
    return (
      <AppShell title="New request">
        <Confirmation
          name={sent.name}
          email={sent.email}
          onAnother={() => {
            form.reset();
            setSent(null);
            setStep(0);
          }}
        />
      </AppShell>
    );
  }

  const footer =
    step < 4 ? (
      <Button size="block" onClick={next}>
        {step === 3 ? 'Review & send' : 'Continue'}
      </Button>
    ) : (
      <Button size="block" onClick={onSend} disabled={formState.isSubmitting}>
        Send the request
      </Button>
    );

  return (
    <AppShell title="New request" footer={footer}>
      <WizardProgress step={step} total={STEP_COUNT} onBack={step === 0 ? undefined : () => goTo(step - 1)} />

      {banner && (
        <p role="alert" className="mt-2 rounded-md bg-coral-tint px-3.5 py-2.5 font-body text-[12.5px] text-coral-text">
          {banner}
        </p>
      )}

      {step === 0 && (
        <section className="mt-3">
          <h1 className="font-display text-[21px] font-semibold tracking-[-0.02em] text-ink">Who are you asking?</h1>
          <p className="mt-1.5 font-body text-[13px] leading-relaxed text-ink-quiet">
            Their name as it should appear on the letter.
          </p>
          <TextField
            label="Referee’s name"
            autoComplete="name"
            error={formState.errors.refereeName?.message}
            {...register('refereeName')}
          />
        </section>
      )}

      {step === 1 && (
        <section className="mt-3">
          <h1 className="font-display text-[21px] font-semibold tracking-[-0.02em] text-ink">
            How did you work together?
          </h1>
          <p className="mt-1.5 font-body text-[13px] leading-relaxed text-ink-quiet">
            This shapes how we frame the letter later.
          </p>
          <div className="mt-3">
            <RelationshipPicker
              value={values.relationshipContext || null}
              onChange={(r: Relationship) => {
                setValue('relationshipContext', r);
                clearErrors('relationshipContext');
              }}
            />
            {formState.errors.relationshipContext && (
              <p role="alert" className="mt-2 text-[11.5px] font-medium text-coral-text">
                {formState.errors.relationshipContext.message}
              </p>
            )}
          </div>
          <TextField label="Company (optional)" autoComplete="organization" {...register('companyName')} />
        </section>
      )}

      {step === REACH_STEP && (
        <section className="mt-3">
          <h1 className="font-display text-[21px] font-semibold tracking-[-0.02em] text-ink">
            How should we reach {firstName}?
          </h1>
          <p className="mt-1.5 font-body text-[13px] leading-relaxed text-ink-quiet">
            We email the invite — you never have to.
          </p>
          <TextField
            label="Email"
            type="email"
            inputMode="email"
            autoComplete="off"
            icon={MailIcon}
            error={formState.errors.refereeEmail?.message}
            {...register('refereeEmail')}
          />
          <PermissionAttestation
            ref={attestationRef}
            label={permissionStatement(values.refereeName)}
            error={formState.errors.refereePermissionConfirmed?.message}
            checked={values.refereePermissionConfirmed}
            onChange={(e) => {
              setValue('refereePermissionConfirmed', e.target.checked);
              clearErrors('refereePermissionConfirmed');
            }}
          />
          <Assurance />
        </section>
      )}

      {step === 3 && (
        <section className="mt-3">
          <h1 className="font-display text-[21px] font-semibold tracking-[-0.02em] text-ink">Add a note</h1>
          <p className="mt-1.5 font-body text-[13px] leading-relaxed text-ink-quiet">
            A short, personal line — it lifts the odds they say yes. Edit it or leave it as is.
          </p>
          <TextArea label={`Note to ${firstName}`} maxLength={400} {...register('personalNote')} />
        </section>
      )}

      {step === 4 && (
        <ReviewStep values={values} firstName={firstName} onEdit={goTo} />
      )}
    </AppShell>
  );
}

/** Step 5 — the summary the candidate confirms before sending. */
function ReviewStep({
  values,
  firstName,
  onEdit,
}: {
  values: WizardValues;
  firstName: string;
  onEdit: (step: number) => void;
}) {
  const rows: { label: string; value: string; step: number }[] = [
    { label: 'Referee', value: values.refereeName.trim() || '—', step: 0 },
    {
      label: 'How you worked together',
      value: [values.relationshipContext, values.companyName.trim()].filter(Boolean).join(' · ') || '—',
      step: 1,
    },
    {
      label: 'We’ll reach them at',
      value: values.refereeEmail.trim() || '—',
      step: 2,
    },
  ];
  return (
    <section className="mt-3">
      <h1 className="font-display text-[21px] font-semibold tracking-[-0.02em] text-ink">Ready to send?</h1>
      <p className="mt-1.5 font-body text-[13px] leading-relaxed text-ink-quiet">
        We’ll email {firstName} the invite. We only use your details for this reference — never for
        anything else.
      </p>

      <dl className="mt-4 divide-y divide-line rounded-lg border border-line bg-surface">
        {rows.map((row) => (
          <div key={row.label} className="flex items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-quiet">{row.label}</dt>
              <dd className="mt-0.5 break-words font-body text-[14px] text-ink">{row.value}</dd>
            </div>
            <button
              type="button"
              onClick={() => onEdit(row.step)}
              className="flex-none rounded-md px-2 py-1 text-[12.5px] font-medium text-sage-text underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25"
            >
              Edit
            </button>
          </div>
        ))}
      </dl>

      {values.personalNote.trim() && (
        <div className="mt-3 rounded-lg border border-line bg-surface-tint px-4 py-3">
          <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-quiet">Your note</p>
          <p className="mt-1 font-body text-[13.5px] italic leading-relaxed text-ink">“{values.personalNote.trim()}”</p>
        </div>
      )}
    </section>
  );
}

/** The warm confirmation + the web "give them a heads-up yourself" affordances (delta a). */
function Confirmation({
  name,
  email,
  onAnother,
}: {
  name: string;
  email: string;
  onAnother: () => void;
}) {
  const first = refereeObject(name);
  const [copied, setCopied] = useState(false);
  const heads =
    `Hi ${first} — I’ve just asked you to be a reference for me through By My Word. ` +
    `You’ll get an email from them with everything you need. It only takes a few minutes, ` +
    `and you approve every word before anything is sent. Thank you — bymyword.com`;

  async function copy() {
    try {
      await navigator.clipboard?.writeText(heads);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(
    'A quick heads-up',
  )}&body=${encodeURIComponent(heads)}`;

  return (
    <div className="flex flex-1 flex-col py-8">
      <div className="text-center">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-sage-tint text-sage-text">
          <svg viewBox="0 0 24 24" fill="none" width={24} height={24} aria-hidden="true">
            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <h1 className="mt-4 font-display text-[22px] font-semibold tracking-[-0.02em] text-ink">
          Request sent to {first}.
        </h1>
        <p className="mx-auto mt-2 max-w-[34ch] font-body text-[14px] leading-relaxed text-ink-quiet">
          We’ve emailed them the invite — you never have to. We’ll tell you the moment they respond.
        </p>
      </div>

      <div className="mt-6 rounded-lg border border-line bg-surface p-4">
        <p className="font-display text-[13.5px] font-semibold text-ink">Want to give {first} a heads-up yourself?</p>
        <p className="mt-1 font-body text-[12.5px] leading-relaxed text-ink-quiet">
          Optional — the invite still comes from us. This just lets them know it’s coming.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={copy}>
            {copied ? 'Copied' : 'Copy message'}
          </Button>
          {email && (
            <Button asChild variant="ghost" size="sm">
              <a href={mailto}>Email {first}</a>
            </Button>
          )}
        </div>
      </div>

      <div className="mt-auto flex flex-col gap-2 pt-8">
        <Button size="block" onClick={onAnother}>
          Request another
        </Button>
        <Button asChild variant="ghost" size="block">
          <Link to="/">Back to your ledger</Link>
        </Button>
      </div>
    </div>
  );
}
